import { forEach } from '@mindkomm/utils';
import smoothscroll from 'smoothscroll-polyfill';

smoothscroll.polyfill();

/**
 * Auto smooth scroll anchor links to their anchor on the same page.
 *
 * This functionality is inspired by default autoscroll functionality found on the web
 * and the book "Inclusive Design Patterns" by Heydon Pickering, p. 148.
 *
 * @version 1.3.1 Fix default option for useTargetFocus.
 * @version 1.3.0 Made functions reusable.
 * @version 1.2.1 Fixed error when the update of the window location hash prevented the scroll.
 * @version 1.2.0 Removed jQuery dependency. Used native smooth scrolling with polyfill. Removed
 *                duration and offset options.
 * @version 1.1.1
 */
export function smoothScroll(selector, options) {
  options = options || {};
  options.useTargetFocus = options.useTargetFocus || false;

  /**
   * Scroller constructor.
   *
   * @param selector
   * @param args
   * @constructor
   */
  function Scroller(selector, args) {
    this.$el = document.querySelectorAll(selector);
    this.init();
  }

  /**
   * Scroller prototype.
   */
  Scroller.prototype = {
    /**
     * Register event handler.
     */
    init: function() {
      forEach(
        this.$el,
        function(item) {
          item.addEventListener('click', this.animateScroll);
        }.bind(this)
      );
    },

    /**
     * Handle scroll animation.
     *
     * @private
     * @param evt
     */
    animateScroll: function(evt) {
      if (evt.defaultPrevented) {
        return;
      }

      if (!isSameResource(window.location, this)) {
        return;
      }

      var target = document.querySelector(this.hash);

      // Bailout if no target could be found.
      if (!target) {
        return;
      }

      // Prevent browser from jumping to the fragment directly.
      evt.preventDefault();

      scrollTo(target, options.useTargetFocus);
    }
  };

  new Scroller(selector, options).init();
}

/**
 * Checks if two URLs are on the same page.
 *
 * @param {Location} url1
 * @param {Location} url2
 * @returns {boolean}
 */
export function isSameResource(url1, url2) {
  var fragmentPattern = /^\//;
  return (
    url1.hostname === url2.hostname &&
    url1.pathname.replace(fragmentPattern, '') ===
      url2.pathname.replace(fragmentPattern, '')
  );
}

export function scrollTo(target, useTargetFocus) {
  if (!target) {
    return;
  }

  var focusTarget = useTargetFocus || false;

  // Trigger a custom event that other plugins can hook into.
  document.dispatchEvent(new CustomEvent('smooth-scroll'));

  if (focusTarget) {
    // Check if target is focusable, otherwise set tabindex to -1 to make it focusable.
    if (target.tabIndex === -1 && !target.getAttribute('tabindex')) {
      target.tabIndex = -1;
    }

    /**
     * Focus target before scrolling.
     *
     * The preventScroll option is rather new and not supported in all browsers and prevents the
     * browser from automatically scrolling to the focused element.
     */
    target.focus({ preventScroll: true });
  }

  // Use native scroll event
  target.scrollIntoView({
    block: 'start',
    behavior: 'smooth'
  });

  // Update location hash
  history.pushState(null, null, '#' + target.id);
}

/**
 * Fixes the scroll position after the page was loaded with a location hash.
 *
 * @link https://stackoverflow.com/a/13857312/1059980
 *
 * @param offset
 */
export function fixScrollPositionAfterLoad(offset) {
  offset = offset || 0;

  const fixPosition = function() {
    var target = document.querySelector(window.location.hash);

    if (target) {
      window.scrollTo(0, target.offsetTop - offset);
    }
  };

  if (!window.location.hash) {
    return;
  }

  window.addEventListener('load', fixPosition);
}
